import {
  SAFETY_REPORT_SEVERITIES,
  SAFETY_REPORT_STATUSES,
  type SafetyReportListItem,
  type SafetyReportSeverity,
  type SafetyReportStatus,
} from "@wallyo/contracts/console/safety";
import { t } from "../../i18n";
import { formatDateTime, formatNumber } from "../../i18n/format";
import { ErrorNotice } from "../../shell/ErrorNotice";
import { SEVERITY_TONE, STATUS_TONE, severityName, statusName } from "./report-format";
import { reportsHref, type ReportFilters } from "./report-route";

export type ReportListState =
  | { status: "loading"; reports: readonly SafetyReportListItem[] }
  | { status: "ready"; reports: readonly SafetyReportListItem[] }
  | { status: "error"; reports: readonly SafetyReportListItem[]; error: unknown };

export type ReportListViewProps = {
  filters: ReportFilters;
  state: ReportListState;
  selectedId: string | null;
  onFilters: (filters: ReportFilters) => void;
  onRetry: () => void;
  /** Null when this person may not open one (WALLIYO-0122). */
  onNewReport?: (() => void) | null;
};

/** The filters, then the reports as the server orders them: severity, status, and whether a review is late. */
export function ReportListView({ filters, state, selectedId, onFilters, onRetry, onNewReport }: ReportListViewProps) {
  return (
    <div className="card report-list">
      {onNewReport ? (
        <div className="top">
          <button className="btn primary" type="button" onClick={onNewReport}>
            {t("reports.new")}
          </button>
        </div>
      ) : null}
      <div className="form queue-filters">
        <div className="field">
          <label htmlFor="report-severity">{t("reports.filter.severity")}</label>
          <select
            id="report-severity"
            value={filters.severity ?? ""}
            onChange={(event) =>
              onFilters({ ...filters, severity: (event.target.value || null) as SafetyReportSeverity | null })
            }
          >
            <option value="">{t("reports.filter.all")}</option>
            {SAFETY_REPORT_SEVERITIES.map((severity) => (
              <option key={severity} value={severity}>
                {severityName(severity)}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="report-status">{t("reports.filter.status")}</label>
          <select
            id="report-status"
            value={filters.status ?? ""}
            onChange={(event) =>
              onFilters({ ...filters, status: (event.target.value || null) as SafetyReportStatus | null })
            }
          >
            <option value="">{t("reports.filter.all")}</option>
            {SAFETY_REPORT_STATUSES.map((status) => (
              <option key={status} value={status}>
                {statusName(status)}
              </option>
            ))}
          </select>
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={filters.reviewOverdue}
            onChange={(event) => onFilters({ ...filters, reviewOverdue: event.target.checked })}
          />{" "}
          {t("reports.filter.reviewOverdue")}
        </label>
      </div>

      {state.status === "error" ? (
        <div className="audit-error">
          <p className="foot">{t("reports.list.loadFailed")}</p>
          <ErrorNotice error={state.error} />
          <div>
            <button className="btn primary" type="button" onClick={onRetry}>
              {t("audit.retry")}
            </button>
          </div>
        </div>
      ) : state.status === "loading" && state.reports.length === 0 ? (
        <div className="empty" role="status">
          <span className="spinner" aria-hidden="true" />
          <span>{t("reports.list.loading")}</span>
        </div>
      ) : state.reports.length === 0 ? (
        <p className="foot" role="status">
          {t("reports.list.empty")}
        </p>
      ) : (
        <div className="rows" aria-label={t("reports.list.aria")}>
          {state.reports.map((report) => {
            const selected = report.safetyReportId === selectedId;
            return (
              <a
                key={report.safetyReportId}
                href={reportsHref(filters, report.safetyReportId)}
                className={`verif-row${selected ? " sel" : ""}${report.reviewOverdue ? " flagged" : ""}`}
                aria-current={selected ? "true" : undefined}
                data-report={report.safetyReportId}
              >
                <span>
                  <b>
                    {report.reference} · {report.subject.name}
                  </b>
                  <br />
                  <span className="meta">
                    {[
                      t(`reports.category.${report.category}` as const),
                      report.reporter
                        ? t("reports.list.by", { name: report.reporter.name })
                        : t(`reports.source.${report.source}` as const),
                      formatDateTime(report.createdAt),
                      report.reportsAboutSubject > 1
                        ? t("reports.list.aboutSubject", { count: formatNumber(report.reportsAboutSubject) })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </span>
                <span className="verif-pills">
                  <span className={`pill ${SEVERITY_TONE[report.severity]}`}>{severityName(report.severity)}</span>
                  <span className={`pill ${STATUS_TONE[report.status]}`}>{statusName(report.status)}</span>
                  {report.restricted ? <span className="pill bad">{t("reports.pill.suspended")}</span> : null}
                  {report.reviewOverdue ? (
                    <span className="pill bad" data-overdue="">
                      {t("reports.pill.reviewOverdue")}
                    </span>
                  ) : null}
                  {report.underLegalHold ? <span className="pill info">{t("reports.pill.legalHold")}</span> : null}
                </span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
